// THREAD TEXT — a thread rendered as markdown you can paste somewhere else.
//
// WHY THIS IS A RENDERING AND NOT A SECOND READING (DECISIONS.md 2026-08-13).
// `threadOrder.js` decides the reading order and `threadSpine.ts` is its one
// licensed server port, pinned by a parity test. This module adds NEITHER — it
// consumes the step tree `extractThread` already returned and turns it into
// characters. It is a second renderer of one reading, the way Thread.js is,
// which is why it lives on the client beside the page it mirrors rather than
// behind a new endpoint: the browser already holds the whole payload when the
// page renders, and a server export would rebuild the graph (~750ms) to
// reproduce data that is in hand.
//
// THE ONE THING IT DOES THAT THE PAGE CANNOT: full content. Thread.js snips
// every step to 200 characters and caps evidence at EVIDENCE_SHOWN, because a
// thread is a reading and not an audit surface. An export is the opposite — it
// is the material leaving, so nothing here truncates and nothing here is
// capped. That difference is the entire reason this file exists; if a future
// edit adds a snip, it has removed the feature.
//
// ⚠ IMPORT CONSTRAINT — THIS MODULE MAY IMPORT ONLY `./threadOrder`.
// (which imports only `./walk`; both are dependency-free). NOT React, NOT
// `./Markdown`, NOT `./snip`, NOT `./EdgeSentence`, NOT `react-router-dom`.
// docker-compose.yaml mounts frontend/src into the backend container WITHOUT
// node_modules, so the unit test loads this file as a bare ES module and there
// is nothing to resolve `react` against. The failure is a module-resolution
// error that reads as unrelated to whatever line caused it. The live
// temptation is `relationPhrase` from EdgeSentence.js, for prettier verbs —
// `phrase()` below is its fallback branch, copied deliberately.
//
// WHAT IS DELIBERATELY ABSENT: trust bands and scores. The blend is
// viewer-weighted, and on this surface an unstaked reader's band is a GLOBAL
// ranking wearing a personalised one (the page says so out loud). A number
// pasted into a draft loses that caveat and keeps the authority, so the export
// carries the claims and the evidence and leaves the arithmetic behind.
//
// NOTHING IS ESCAPED. This is markdown source going into a markdown target;
// escaping it would corrupt the text the author wants to edit. Known wart: a
// claim whose text opens with `#` renders downstream as a heading.
import { allThreadNodes } from './threadOrder';

// One branch level. FOUR spaces, not two: old Reddit requires four to nest a
// list and new Reddit accepts four, and Reddit is the first destination this
// was built for. A renderer that flattens the indent still produces readable
// text, which is why evidence is LABELLED below and branches are not.
const INDENT = '    ';

// A markdown HARD line break: two trailing spaces. Markdown joins consecutive
// lines of a paragraph into one, so without this a step's byline and its loop
// marker render run together on the end of the claim — correct as plain text,
// wrong the moment anything renders it. Two spaces rather than a trailing
// backslash because old Reddit supports one of those and not the other.
const BREAK = '  \n';

// Continuation lines of a multi-line claim have to carry the block's own
// indent or the second line falls out of its bullet. Claims are usually one
// line; imported and dictated content is not — and where the author put a line
// break, the export keeps one.
const indentBlock = (text, pad) =>
  String(text ?? '')
    .trim()
    .split('\n')
    .map((line, i) => (i === 0 ? line : pad + line))
    .join(BREAK);

// The fallback branch of relationPhrase (EdgeSentence.js), reimplemented here
// under the import constraint above. The VERBS map is not copied on purpose —
// two verb tables drift, and every row that reaches this module is a
// `supports` or `contradicts` edge (EVIDENCE_RELATIONS in backend/lib/threads.ts),
// whose raw names are already the right English word. The negative form is the
// documented fallback contract and is unreachable for those two in practice:
// structural relations are +1-only at the write door.
const phrase = (name, polarity) => {
  const raw = String(name || '').replace(/_/g, ' ');
  return polarity === -1 ? `not: ${raw}` : raw;
};

// Names, or nothing. The page's Byline renders the literal word "unattributed"
// because on screen an absent line is the false-absence defect — a quoted claim
// reading as the author's own belief. In a document that distinction is carried
// once, in the header line, instead of forty times down the page: forty
// repetitions of "unattributed" under an author's own claims is noise that gets
// deleted by hand, and a convention stated once survives the edit.
const bylineOf = (authors) => {
  const names = (authors || [])
    .map((a) => String((a && a.content) ?? '').trim())
    .filter(Boolean);
  return names.length ? names.join(', ') : '';
};

const votes = (e) => Number(e.up_votes || 0) + Number(e.down_votes || 0);

/**
 * Index the payload's `attachments` under whichever of their endpoints is on
 * the thread — both, when an edge joins two steps, so the row renders under
 * each with "this" flipping ends.
 *
 * Lives here rather than in Thread.js so the page and the export cannot
 * disagree about which evidence belongs to which step, or in what order.
 * Sorted by vote VOLUME (not score): the most-judged rows first, agreement and
 * disagreement alike.
 */
export function evidenceByStep(attachments, nodeIds) {
  const m = new Map();
  const onThread = nodeIds instanceof Set ? nodeIds : new Set(nodeIds || []);
  const rows = [...(attachments || [])].sort((a, b) => votes(b) - votes(a));
  for (const e of rows) {
    for (const end of [e.source, e.target]) {
      if (!onThread.has(end)) continue;
      if (!m.has(end)) m.set(end, []);
      m.get(end).push(e);
    }
  }
  return m;
}

// One evidence row as a sentence, mirroring EdgeSentence: source verb target,
// with the end this step sits on rendered as the word "this". The far end's
// byline is the far end's, which is the same choice the page's evidence rows
// make — the interesting attribution is the one you do not already know.
//
// LABELLED `evidence:` on purpose, while branches are not labelled at all. The
// 2026-08-10 grammar ruling says indentation is the branch's whole address, so
// naming one would contradict a live decision; but a bullet under a step could
// be either, and a reader of the TEXT has lost the border and the colour that
// distinguish them on screen. Labelling the one that is not ruled silent is
// what keeps both legible.
function evidenceLine(e, nodeId, pad) {
  const focusIsSource = e.source === nodeId;
  const farContent = focusIsSource ? e.target_content : e.source_content;
  const farAuthors = focusIsSource ? e.target_authored_by : e.source_authored_by;
  const verb = phrase(e.relation_name, e.polarity);
  const quoted = `“${indentBlock(farContent, pad)}”`;
  const sentence = focusIsSource ? `this ${verb} ${quoted}` : `${quoted} ${verb} this`;
  const who = bylineOf(farAuthors);
  // An unvoted edge is a SUGGESTION — very often one an agent minted, since
  // every model write lands inert. Marking it is what stops the export
  // laundering a suggestion into a citation the moment it leaves the app.
  const inert = votes(e) === 0 ? ' *(unvoted)*' : '';
  return `${pad}- evidence: ${sentence}${who ? ` *— ${who}*` : ''}${inert}`;
}

// A thread step is a link OUT, never an expansion — the payload is one closure
// from one root, so there is nothing here to expand it from. Same call the page
// makes, for the same reason.
const threadLink = (id, origin) => `${origin}/thread/${id}`;

function renderStep(step, depth, ctx, out) {
  const { node, index, repeat } = step;
  const heading = node.object_type === 'heading';
  const nested = node.object_type === 'thread';
  const inBranch = depth > 0;
  // A branch step is a bullet one level in; its own sub-blocks sit one level
  // further, which is also where its branches' bullets land.
  const bullet = inBranch ? INDENT.repeat(depth - 1) : '';
  const pad = INDENT.repeat(depth);
  const content = String(node.content ?? '').trim();

  // A step's own lines are ONE markdown block, joined by hard breaks. Pushed as
  // separate lines they would form a single paragraph, and the claim, its
  // byline and its loop marker would render as one run-on sentence — a defect
  // invisible in the raw text and obvious the moment anything renders it.
  const lines = [];

  if (heading) {
    // NO number and NO byline, exactly as the page has it: a heading is
    // editorial framing over what follows, not a thing you read as step four,
    // and numbering it would make the count of steps disagree with the count of
    // things a reader actually reads. Inside a branch it cannot be an `##` —
    // a heading nested in a list is not a heading — so it carries its weight
    // as bold.
    lines.push(inBranch ? `${bullet}- **${indentBlock(content, pad)}**` : `## ${content}`);
  } else if (inBranch) {
    // Branch steps carry no number either (2026-08-10): every branch line
    // restarts at 1, so five sibling claims under a heading would each read
    // "1" — five markers carrying no information.
    lines.push(`${bullet}- ${indentBlock(content, pad)}`);
  } else {
    lines.push(`**${index}.** ${indentBlock(content, pad)}`);
  }

  if (!heading && !nested) {
    const who = bylineOf(node.authored_by);
    if (who) lines.push(`${pad}*— ${who}*`);
  }

  if (nested) {
    lines.push(`${pad}*an authored chain of its own — ${threadLink(node.id, ctx.origin)}*`);
  }

  if (repeat) {
    lines.push(
      `${pad}*↺ this step is already step ${repeat} of this thread — the thread loops back here*`
    );
  } else if (step.duplicate) {
    lines.push(`${pad}*⧉ this claim also appears elsewhere in this thread*`);
  }

  out.push('');
  out.push(lines.join(BREAK));

  // A repeat is a pointer, not a position: its evidence rendered at its first
  // occurrence and the traversal hands back no branches. Stop here.
  if (repeat) return;

  // UNCAPPED, unlike the page's EVIDENCE_SHOWN. The page truncates to a door
  // ("+N more on the object page →"); an export has no door to point at, and
  // silently dropping evidence on the way out is the failure this codebase
  // names in four other places.
  const rows = ctx.evidence.get(node.id) || [];
  if (rows.length) {
    out.push('');
    for (const e of rows) out.push(evidenceLine(e, node.id, pad));
  }
}

// Evidence first, then branches — the page's own order (evidence renders inside
// the step card, branches render after it), and the order that keeps a branch's
// bullets from reading as more evidence for its parent.
function renderSteps(steps, depth, ctx, out) {
  for (const step of steps) {
    renderStep(step, depth, ctx, out);
    if (step.repeat) continue;
    for (const b of step.branches || []) renderSteps(b.steps, depth + 1, ctx, out);
  }
}

// The thread's own URL is the only one passed in; sibling links are built off
// its origin. A parameter rather than a `window` read, which is what lets this
// module be unit-tested outside a browser.
const originOf = (url) => {
  const m = /^(https?:\/\/[^/]+)/.exec(String(url || ''));
  return m ? m[1] : '';
};

/**
 * Render a thread as markdown.
 *
 *   thread     — the payload's `thread` (content, id, published, audience_targets)
 *   steps      — extractThread's step tree, exactly as the page renders it
 *   evidence   — evidenceByStep's Map, shared with the page
 *   withheld   — count of steps outside the reader's visibility
 *   truncated  — the closure hit its depth cap
 *   url        — this thread's address, or '' when there is none to give
 */
export function threadToMarkdown({
  thread,
  steps = [],
  evidence = new Map(),
  withheld = 0,
  truncated = false,
  url = '',
}) {
  const ctx = { evidence, origin: originOf(url) };
  const out = [];
  const title = String((thread && thread.content) ?? '').trim();
  // allThreadNodes, not steps.length — branches and repeats count, and this is
  // the same function the page's "N steps" reads, so the two cannot disagree.
  const total = allThreadNodes(steps).size;

  out.push(`# ${title}`);
  out.push('');
  out.push(
    `*A thread of ${total} step${total === 1 ? '' : 's'}, exported from Worldview. ` +
      `Steps with no name are the thread author's own.*`
  );

  renderSteps(steps, 0, ctx, out);

  out.push('');
  out.push('---');
  out.push('');

  // The honest footer, hard-broken for the same reason a step's is: three
  // pushed lines are one markdown paragraph, and "…not shared with you. …cut
  // here. …only opens for you:" renders as a single run-on sentence.
  //
  // Both conditions are ones the page already reports on screen, and an export
  // that dropped them would hand over a partial document that looks complete —
  // the exact failure countWithheld exists to prevent on the server side.
  const footer = [];
  if (withheld > 0) {
    footer.push(
      `*This thread continues into ${withheld} step${withheld === 1 ? '' : 's'} ` +
        `that are not shared with you — what is above is the part that is.*`
    );
  }
  if (truncated) {
    footer.push('*This thread is longer than one sitting — it was cut here.*');
  }

  // Where this came from, and whether that link works for anyone else. A
  // pasted draft outlives the tab it came from; a link that silently 404s for
  // every reader is worse than no link.
  const shared =
    (thread && thread.published) ||
    ((thread && thread.audience_targets) || []).length > 0;
  if (url) {
    footer.push(shared ? url : `*Not shared — this link only opens for you:* ${url}`);
  }
  if (footer.length) out.push(footer.join(BREAK));

  // Collapse the runs of blank lines the block-per-push style produces, and
  // finish with exactly one newline.
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}
